import type { ReactNode } from "react";

import { BasiqClient } from "../elements/basiq/basiq-client";
import { useBankConnectWithPlaid } from "../elements/use-bank-connect";
import { useBankConnectionLogoResolvers, useBankConnectionLogos } from "../elements/use-bank-connect-logos";
import type { PlaidBankConnectSchema } from "../api-types/bankconnect_schemas";

export interface BankConnectProps extends PlaidBankConnectSchema {
  basiqClient?: BasiqClient;
}

export interface PlaidBankConnectHookFactoryOptions {
  minBanksConnectedCallback?: () => void;
  onConnectionCallback?: () => void;
  onReconnectionCallback?: () => void;
  messageSuccess?: (message: string) => void;
  messageError?: (message: ReactNode) => void;
  onPlaidOpenCallback?: () => void;
  onPlaidExitCallback?: (metadata?: any) => void;
}

const minBanksConnected = 1;

export function plaidBankConnectHookFactory(
  bankConnectProps: BankConnectProps,
  options: PlaidBankConnectHookFactoryOptions = {}
) {
  const {
    minBanksConnectedCallback,
    onConnectionCallback,
    onReconnectionCallback,
    messageSuccess,
    messageError,
    onPlaidOpenCallback,
    onPlaidExitCallback,
  } = options;
  return function useBankConnect() {
    const {
      connectAccountPath,
      accountReconnectedPath,
      plaidLinkRedirectUri,
      getLinkTokenPath,
      getUpdateTokenPath,
      getInstitutionLogoPath,
      bankConnections: initialBankAccountConnectors = [],
      basiqClient,
    } = bankConnectProps;

    /**
     * plaid link handling
     */
    const { bankConnections, initiateLinkMode, initiateUpdateMode } = useBankConnectWithPlaid({
      bankConnections: initialBankAccountConnectors,
      connectAccountPath,
      accountReconnectedPath,
      plaidLinkRedirectUri,
      getLinkTokenPath,
      getUpdateTokenPath,
      onPlaidOpen: onPlaidOpenCallback,
      onPlaidExit: onPlaidExitCallback,
      onAccountConnected: onConnectionCallback,
      onAccountReconnected: onReconnectionCallback,
      messageSuccess,
      messageError,
    });

    if (bankConnections.length >= minBanksConnected) {
      minBanksConnectedCallback && minBanksConnectedCallback();
    }

    const logoResolvers = useBankConnectionLogoResolvers({ getInstitutionLogoPath, basiqClient });

    const { logoLoadingStates } = useBankConnectionLogos({
      bankConnections,
      resolvers: logoResolvers,
    });

    return {
      bankConnections,
      startConnect: initiateLinkMode,
      startReconnect: initiateUpdateMode,
      logoLoadingStates,
      providerName: "Plaid",
    };
  };
}
